import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Typography, TextField, Button, Spinner, Alert } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { adminApiClient, type VendorApplicationItem } from '../lib/adminApiClient';
import { NEUTRAL_BTN } from '../lib/buttonStyles';
import { StoreChip, StageChip } from '../components/VendorChips';
import { PIPELINE_STAGES, TERMINAL_STAGES } from '../lib/vendorChips';
import { duplicateWarningLabel } from '../lib/vendorApplicationLifecycle';

/**
 * Vendor applications — board / kanban view (Figma 300:2). A column per pipeline
 * stage with application cards; a muted Declined / Withdrawn lane below.
 */

function nameOf(app: VendorApplicationItem): string {
  return app.businessInfo.businessName || 'Unnamed vendor';
}
function daysInStage(app: VendorApplicationItem): string {
  if (app.status === 'approved') return '✓ Live';
  const days = Math.max(0, Math.floor((Date.now() - new Date(app.updatedAt).getTime()) / 86400000));
  return days === 0 ? 'today' : `${days}d in stage`;
}

function Centered({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>{children}</Box>;
}

export default function VendorApplicationsBoard() {
  const caps = useCapabilities();
  const canView = caps.has('vendor:read');
  const canReview = caps.has('vendor:review');
  const navigate = useNavigate();

  const [items, setItems] = useState<VendorApplicationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listVendorApplications({ status: 'all', limit: 200 });
      setItems(res.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load vendor applications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  const byStatus = useMemo(() => {
    const map = new Map<string, VendorApplicationItem[]>();
    const q = search.trim().toLowerCase();
    for (const a of items) {
      if (q && !nameOf(a).toLowerCase().includes(q)) continue;
      const list = map.get(a.status) ?? [];
      list.push(a);
      map.set(a.status, list);
    }
    return map;
  }, [items, search]);

  if (caps.loading)
    return (
      <Centered>
        <Spinner color="brand" />
      </Centered>
    );
  if (!canView)
    return <Alert severity="error">You don’t have permission to view vendor applications.</Alert>;

  const terminalCount = TERMINAL_STAGES.reduce((n, s) => n + (byStatus.get(s.status)?.length ?? 0), 0);

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Vendor applications
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Vendor onboarding pipeline — review the applicant, build their price adapter, and approve
        them onto the marketplace.
      </Typography>

      {!canReview && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">View-only access — you can browse but can’t make changes.</Alert>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search vendors by business name"
          ariaLabel="Search vendors"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 240 }}
        />
        <Button
          ariaLabel="Switch to list view"
          buttonText="List view"
          variant="outlined"
          size="small"
          sx={NEUTRAL_BTN}
          onClick={() => navigate('/vendor-applications')}
        />
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Centered>
          <Spinner color="brand" />
        </Centered>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 1.25, overflowX: 'auto', pb: 1, alignItems: 'stretch' }}>
            {PIPELINE_STAGES.map((stage) => {
              const cards = byStatus.get(stage.status) ?? [];
              return (
                <Column key={stage.status} label={stage.label} count={cards.length}>
                  {cards.map((app) => (
                    <Card key={app.applicationId} app={app} onOpen={() => navigate(`/vendor-applications/${app.applicationId}`)} />
                  ))}
                </Column>
              );
            })}
          </Box>

          {terminalCount > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: '#9aa3ab', letterSpacing: 0.5 }}
              >
                CLOSED — DECLINED / WITHDRAWN
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
                {TERMINAL_STAGES.flatMap((s) => byStatus.get(s.status) ?? []).map((app) => (
                  <ClosedCard
                    key={app.applicationId}
                    app={app}
                    onOpen={() => navigate(`/vendor-applications/${app.applicationId}`)}
                  />
                ))}
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

function Column({
  label,
  count,
  children,
}: {
  label: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        flex: '1 1 124px',
        minWidth: 120,
        backgroundColor: '#f1f4f2',
        borderRadius: 2,
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>
          {label}
        </Typography>
        <Box
          component="span"
          sx={{
            backgroundColor: '#e2e8f0',
            color: '#64748b',
            borderRadius: '8px',
            px: 0.75,
            fontSize: '10px',
            fontWeight: 700,
          }}
        >
          {count}
        </Box>
      </Box>
      {children}
    </Box>
  );
}

function Card({ app, onOpen }: { app: VendorApplicationItem; onOpen: () => void }) {
  return (
    <Box
      onClick={onOpen}
      sx={{
        backgroundColor: '#fff',
        border: '1px solid #e6ebe8',
        borderRadius: '8px',
        p: 1.25,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: '12px', color: '#1f2937', lineHeight: 1.3 }}>
        {nameOf(app)}
      </Typography>
      {duplicateWarningLabel(app.duplicateBusinessWarning) && (
        <Typography sx={{ fontSize: '10px', color: '#9A6700', fontWeight: 700 }}>
          {duplicateWarningLabel(app.duplicateBusinessWarning)}
        </Typography>
      )}
      <Box>
        <StoreChip businessType={app.businessInfo.businessType} />
      </Box>
      <Typography
        sx={{ fontSize: '10px', color: app.status === 'approved' ? '#15803d' : '#9aa3ab' }}
      >
        {daysInStage(app)}
      </Typography>
    </Box>
  );
}

function ClosedCard({ app, onOpen }: { app: VendorApplicationItem; onOpen: () => void }) {
  const reason =
    app.status === 'declined'
      ? `${app.declinedReason ?? 'declined'}`
      : 'withdrawn by applicant';
  return (
    <Box
      onClick={onOpen}
      sx={{
        flex: '1 1 300px',
        minWidth: 280,
        backgroundColor: '#fafbfb',
        border: '1px solid #e6ebe8',
        borderRadius: '8px',
        p: 1.5,
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#475569' }}>
          {nameOf(app)}
        </Typography>
        {duplicateWarningLabel(app.duplicateBusinessWarning) && (
          <Typography sx={{ fontSize: '10px', color: '#9A6700', fontWeight: 700 }}>
            {duplicateWarningLabel(app.duplicateBusinessWarning)}
          </Typography>
        )}
        <StoreChip businessType={app.businessInfo.businessType} />
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <StageChip status={app.status} />
        <Typography sx={{ fontSize: '10px', color: '#9aa3ab', mt: 0.5 }}>{reason}</Typography>
      </Box>
    </Box>
  );
}
